import { useState } from 'react';
import moment from 'moment';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import useIsMobile from '../../hooks/useIsMobile';
import { 
  getLocationOptions, 
  getLocationColor 
} from '../../helpers/locations';
import CustomDialog from '../common/CustomDialog';
import CalendarDayView from './CalendarDayView';
import CalendarWeekView from './CalendarWeekView';
import BlockTimeForm from './BlockTimeForm';
import SessionFormV2 from '../sessions/SessionFormV2';
import SelectPatientForm from '../scheduling/SelectPatientForm';
import NewSessionButton from '../sessions/NewSessionButton';
import { getEventBlocks, getAvailabilityBlocks } from './calendarUtils';
import useError from '../../hooks/useError';
import { useCreateVacationTimeMutation } from '../../redux/services/vacationTimeApi';

const CalendarScreen = ({
  date,
  sessions,
  events = [],
  availabilities = [],
  calendarView = 'week',
  metrics = [],
  useSummaries = false,
  boxStyles = {
    backgroundColor: 'white',
    // overflowY: 'hidden',
    borderRadius: 3,
    boxShadow: 1,
    border: 1,
    borderColor: 'divider',
  },
  startHour = 8,
  endHour = 20,
  onDateChange = (e, date) => console.log('Change date', date),
  onCalendarViewChange = (e, view) => console.log('Change calendar view', view),
} = {}) => {
  const isMobile = useIsMobile();
  const [sessionId, setSessionId] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [createSessionDialogOpen, setCreateSessionDialogOpen] = useState(false);
  const error = useError();
  const [createVacationTime] = useCreateVacationTimeMutation();
  const selectedSession = sessions?.find(s => s.id === sessionId);
  const eventBlocks = (
    events && events.length
    ? getEventBlocks({
        timeStart: moment(date).startOf(calendarView).toISOString(),
        timeEnd: moment(date).endOf(calendarView).toISOString(),
        startHour,
        endHour,
      })(events)
    : []
  );
  const availabilityBlocks = (
    availabilities && availabilities.length
    ? getAvailabilityBlocks({ date, startHour, endHour })(availabilities)
    : []
  );
  return (
    <Box style={{paddingLeft: isMobile ? '0em' : '1em', paddingBottom: '1em'}}>
      <Box style={{paddingBottom: '2em'}}>
        <Grid container>
          <Grid item xs={12} md={9} spacing={2}>
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Box style={{marginTop: '-1em'}}>
                  <NewSessionButton 
                    sessionVersion='2'
                    buttonText='Schedule'
                    size='small'
                  />
                </Box>
                <Box>
                  <Button 
                    color='info'
                    variant='contained'
                    size='small'
                    onClick={() => setEventDialogOpen(true)}
                    startIcon={<AddIcon/>}
                  >
                    Block Time
                  </Button>
                </Box>
                <Box>
                  <ButtonGroup>
                    <Button
                      size='small'
                      variant={calendarView === 'week' ? 'contained' : 'outlined'}
                      color='primary'
                      onClick={e => onCalendarViewChange(e, 'week')}
                    >
                      Week
                    </Button>
                    <Button
                      size='small'
                      variant={calendarView === 'day' ? 'contained' : 'outlined'}
                      color='primary'
                      onClick={e => onCalendarViewChange(e, 'day')}
                      style={{minWidth: '60px', fontSize: '0.875rem'}}
                    >
                      Day
                    </Button>
                  </ButtonGroup>
                </Box>
              </Stack>
            </Stack>

          </Grid>

        </Grid>
      </Box>
      <Grid container spacing={1} sx={{ml: {xs: -1}}}>
        <Grid item sx={12} md={9}>
          {calendarView === 'week' && 
            <CalendarWeekView
              date={date}
              sessions={sessions}
              eventBlocks={eventBlocks}
              availabilityBlocks={availabilityBlocks}
              boxStyles={boxStyles}
              onDateChange={onDateChange}
              onEditSession={(e, id) => setSessionId(id)}
              useSummaries={useSummaries}
              startHour={startHour}
              endHour={endHour}
            />
          }
          {
            calendarView === 'day' &&
            <CalendarDayView
              date={date}
              sessions={sessions}
              eventBlocks={eventBlocks}
              availabilityBlocks={availabilityBlocks}
              boxStyles={boxStyles}
              onDateChange={onDateChange}
              onEditSession={(e, id) => setSessionId(id)}
              useSummaries={useSummaries}
              startHour={startHour}
              endHour={endHour}
            />
          }
        </Grid>
        {/* hide on mobile */}
        <Grid item sx={{display: {xs: 'none', sm: 'none', md: 'flex'}}} md={3}>
          <Stack spacing={2} style={{width: '100%'}}>
            <Box sx={{...boxStyles, p: 2}}>
              <Typography variant='h2'>Location Types</Typography>
              <Stack spacing={0.5} pt={1}>
                {getLocationOptions().map(loc => (
                  <Stack direction='row' spacing={1}>
                    <Box
                      sx={{
                        width: 20, 
                        height: 20, 
                        backgroundColor: getLocationColor(loc.value),
                        borderRadius: 3,
                      }}
                    />
                    <Typography variant='body1'>{loc?.emoji || null} {loc.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box sx={{...boxStyles, p: 2}}>
              <Typography variant='h2'>This Week</Typography>
              <Stack spacing={2} pt={1}>
                {metrics.map(metric => 
                  <Stack direction='row' spacing={1}>
                    <Typography variant='h1' style={{color: config().colors.secondary}}>
                      {metric.value}
                    </Typography>
                    <Typography variant='body' style={{paddingTop: '0.5em'}}>{metric.label}</Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <CustomDialog
        open={!!sessionId}
        onClose={() => setSessionId(null)}
        fullWidth={true}
        maxWidth='md'
        showCloseButton={true}
        title='Session Details'
      >
        {
          !!selectedSession
          ? <SessionFormV2
            session={selectedSession}
            isPatient={false}
            isEditing={true}
            onClose={() => setSessionId(null)}
            afterSubmit={() => setSessionId(null)}
          />
          : <></>
        }
      </CustomDialog>
      <CustomDialog
        open={createSessionDialogOpen}
        onClose={() => setCreateSessionDialogOpen(false)}
        fullWidth={true}
        maxWidth='sm'
      >
        <SelectPatientForm title='Find a Patient' />
      </CustomDialog>
      <CustomDialog
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        fullWidth={true}
        maxWidth='sm'
        title='Block Time'
      >
        <BlockTimeForm onSuccess={() => setEventDialogOpen(false)} />
      </CustomDialog>
    </Box>
  );
};

export default CalendarScreen;